import Vue from "vue";
/* global Keycloak */ // loaded via <script> tag in public/index.html
import { apiGateway } from "@/shared/apiGateway";

let instance;

export const getInstance = () => instance;

const globalRole = 1; // Admin role value from backend profile

/** Creates (or returns) the Keycloak-backed auth instance */
export const useKeycloak = (options) => {
  if (instance) return instance;

  const keycloak = new Keycloak({
    url: options.url,
    realm: options.realm,
    clientId: options.clientId
  });

  // The instance is a Vue object so authGuard can use $watch("loading", ...)
  instance = new Vue({
    data() {
      return {
        loading: true,
        isAuthenticated: false,
        user: {},
        profile: undefined,
        error: null
      };
    },
    methods: {
      /** Returns the current access token, refreshing if it will expire within 30 seconds */
      async getTokenSilently() {
        await keycloak.updateToken(30);
        return keycloak.token;
      },
      /** Keycloak uses one token per realm — acquireToken delegates to getTokenSilently */
      async acquireToken() {
        return this.getTokenSilently();
      },
      /** Redirects the user to the Keycloak login page */
      loginWithRedirect() {
        keycloak.login();
      },
      async setProfile() {
        try {
          this.profile = await apiGateway.apiGatewayGET("api/identity/");
        } catch {
          alert("Service unavailable");
        }
      },
      logout() {
        keycloak.logout();
      },
      isInGlobalRole() {
        return this.profile.roles.includes(globalRole);
      },
      isInRole(role) {
        return this.profile.roles.includes(role);
      }
    },
    async created() {
      try {
        const authenticated = await keycloak.init({
          onLoad: "login-required",
          pkceMethod: "S256",
          checkLoginIframe: false,
          responseMode: "query"
        });
        this.isAuthenticated = authenticated;
        if (authenticated) {
          this.user = keycloak.tokenParsed;
          await this.setProfile();
          // Proactively refresh the token before it expires
          setInterval(() => keycloak.updateToken(30), 10000);
        }
      } catch (e) {
        this.error = e;
      } finally {
        this.loading = false;
      }
    }
  });

  return instance;
};

export const KeycloakPlugin = {
  install(Vue, options) {
    Vue.prototype.$auth = useKeycloak(options);
  }
};
